import * as React from 'react';
import styled from 'styled-components';
import { useFormikContext } from 'formik';
import AddressLookup from '../../../commonComponents/AddressLookup';
import { australianStates } from '../../../core/domain';
import { getCountryCode } from '../../../core/countryList';
import './AddressLookupField.less';

const AddressLookupField = (props: Props) => {
	const baseFieldName = props.baseFieldName ?? 'addressHistory';
	const [addressLookup, setAddressLookup] = React.useState('');

	const { setFieldValue } = useFormikContext();

	const handleAddressLookupChange = (address: string, addressTuples: string[][]) => {
		setAddressLookup(address);

		if (addressTuples != null) {
			const fieldNames = getAddressHistoryFieldNames();
			const addressTypesToFieldNames = getAddressTypesToFieldNames();
			const isUkAddress = addressTuples.find((x) => x[0] === 'country' && x[1] === 'United Kingdom') !== undefined;
			if (isUkAddress) {
				addressTypesToFieldNames.sublocality = 'locality';
				addressTypesToFieldNames.locality = 'locality';
				addressTypesToFieldNames.administrative_area_level_2 = 'state';
				addressTypesToFieldNames.administrative_area_level_1 = null;
			}

			addressTuples.forEach((tuple) => {
				const [type, value] = tuple;
				let updatedValue = value;
				const fieldName = addressTypesToFieldNames[type];
				// A null field name is a type we deliberately ignore. An undefined field name is a type we do not
				// know about, which Google keeps adding, so ignore those too rather than failing the whole lookup.
				if (fieldName != null) {
					if (fieldName === 'country') {
						updatedValue = getCountryCode(mapGoogleMapCountryNameToIsoCountryName(updatedValue));
					} else if (fieldName === 'state') {
						updatedValue = getStateCode(updatedValue);
					}
					setFieldValue(`${baseFieldName}.${fieldName}`, updatedValue);
					// Remove the field name from the array once the value is set, so that below all remaining fields are set to an empty string.
					fieldNames.splice(fieldNames.indexOf(fieldName), 1);
				}
			});

			// Some countries (e.g. Egypt, Brazil) put the suburb in sublocality_level_1 rather than in any of the
			// types mapped to suburb above, which would leave the required Suburb / Town field empty. Only fall
			// back to it when nothing else filled the field, so countries that already map correctly are unaffected.
			// UK addresses are excluded because they deliberately leave suburb empty and use the locality field.
			if (!isUkAddress && fieldNames.includes('suburb')) {
				const subLocalityLevel1 = addressTuples.find((x) => x[0] === 'sublocality_level_1');
				if (subLocalityLevel1 !== undefined) {
					setFieldValue(`${baseFieldName}.suburb`, subLocalityLevel1[1]);
					fieldNames.splice(fieldNames.indexOf('suburb'), 1);
				}
			}

			// Set the remaining fields to empty string.
			fieldNames.forEach((x) => {
				setFieldValue(`${baseFieldName}.${x}`, null);
			});
		}
	};

	return (
		<>
			<div className="k-form-field addressLookupField">
				<Label className="label" htmlFor="locationSearchInput">
					Address Lookup
				</Label>
				<AddressLookup
					value={addressLookup}
					onChange={handleAddressLookupChange}
					countryRestriction={props.countryRestriction}
				/>
			</div>
			{/* This special field is as wide as 2 form fields, add following div for styling. */}
			<div />
		</>
	);
};

interface Props {
	baseFieldName: string | undefined;
	countryRestriction: string | undefined;
}

export default AddressLookupField;

const Label = styled.label`
	display: block;
`;

const getStateCode = (value: string) => {
	const auState = australianStates.find((x) => x.name === value);
	if (auState === undefined) return value;
	return auState.code;
};

const getAddressHistoryFieldNames = () => {
	return [
		'unitNumber',
		'buildingName',
		'streetNumber',
		'streetName',
		'locality',
		'suburb',
		'state',
		'country',
		'postcode'
	];
};

const getAddressTypesToFieldNames = () => {
	return {
		street_address: null,
		campground: null as string,
		floor: null as string,
		establishment: null as string,
		parking: null as string,
		street_number: 'streetNumber',
		subpremise: 'unitNumber',
		point_of_interest: null,
		post_box: null,
		room: null,
		natural_feature: null,
		airport: null,
		bus_station: null,
		train_station: null,
		transit_station: null,
		park: null,
		plus_code: 'streetName',
		premise: 'buildingName', // Assume that premise can be used as building Name.
		route: 'streetName',
		intersection: null,
		neighborhood: null,
		// Assume that these suburb address types will never exist at the same time.
		administrative_area_level_5: 'suburb',
		sublocality: 'suburb',
		locality: 'suburb',
		postal_town: 'suburb',
		// political: null, not needed due to custom code that specifically ignores it before this getAddressTypesToFieldNames object would be relevant
		colloquial_area: null,

		// ignore
		sublocality_level_5: null,
		sublocality_level_4: null,
		sublocality_level_3: null,
		sublocality_level_2: null,
		sublocality_level_1: null,

		// Not sure what this is, but some countries do use it. I'm assuming it doesn't contain town.
		administrative_area_level_4: null,
		administrative_area_level_3: null,

		administrative_area_level_2: null,
		administrative_area_level_1: 'state',
		country: 'country',
		postal_code: 'postcode',
		postal_code_suffix: null
	} as Record<string, string>;
};

export const mapGoogleMapCountryNameToIsoCountryName = (countryName: string) => {
	if (countryName === 'United States') {
		return 'United States of America';
	}
	if (countryName === 'United Kingdom') {
		return 'United Kingdom';
	}
	if (countryName === 'Vietnam') {
		return 'Viet Nam';
	}
	return countryName;
};
